import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useAssignMutation from 'hooks/mutations/useAssignMutation';
import useFileUploadMutation from 'hooks/mutations/useFileUploadMutation';
import useSendActivityLogMessageMutation from 'hooks/mutations/useSendActivityLogMessageMutation';
import useSendPowerOfAttorneyEmailMutation from 'hooks/mutations/useSendPowerOfAttorneyEmailMutation';
import useUpdateWorkshopVerificationDocsMutation from 'hooks/mutations/useUpdateWorkshopVerificationDocsMutation';
import useWorkshopVerificationStateMutation from 'hooks/mutations/useUpdateWorkshopVerificationStateMutation';
import useWorkshopVerificationRequestQuery from 'hooks/useWorkshopVerificationRequestQuery';
import { RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTriggerGenericSpfVerificationMailMutation } from '../queries/useTriggerGenericSpfVerificationMailMutation';
import { VerificationStatus, type WorkshopVerificationRequest } from '../types';

type UseVerificationActionsParams = {
  workshopId: string | undefined;
  verificationData: WorkshopVerificationRequest | undefined;
  currentUserId: number | undefined;
  topRef: RefObject<HTMLDivElement | null>;
};

export function useVerificationActions({ workshopId, verificationData, currentUserId, topRef }: UseVerificationActionsParams) {
  const { t } = useTranslation();
  const toast = useToast({ ...defaultToastOptions });
  const queryClient = useQueryClient();

  const workshopVerificationId = verificationData?.id;

  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useWorkshopVerificationRequestQuery.getKey(workshopId!) });

  // --- Mutations ---
  const updateDocsMutation = useUpdateWorkshopVerificationDocsMutation(workshopVerificationId);
  const clarificationMutation = useWorkshopVerificationStateMutation(workshopVerificationId, 'clarification');
  const approveMutation = useWorkshopVerificationStateMutation(workshopVerificationId, 'approve');
  const assignMutation = useAssignMutation(verificationData?.open_task?.id as number, useWorkshopVerificationRequestQuery.getKey(workshopId));
  const triggerPoaMutation = useSendPowerOfAttorneyEmailMutation(workshopVerificationId);
  const triggerSpfMailMutation = useTriggerGenericSpfVerificationMailMutation(workshopVerificationId);
  const fileUploadMutation = useFileUploadMutation();
  const { mutate: sendMessage } = useSendActivityLogMessageMutation(null, null, useWorkshopVerificationRequestQuery.getKey(workshopId));

  // --- Derived State ---
  const derived = useMemo(() => {
    const status = verificationData?.status;
    const openTask = verificationData?.open_task;
    const isOpenTaskAssignedToCurrentUser = openTask?.assignee?.id === currentUserId;

    const isCompleted = status === VerificationStatus.COMPLETED;
    const isRejected = status === VerificationStatus.REJECTED;
    const waitingOnPoa = status === VerificationStatus.WAITING_ON_POA;
    const isInClarificationStep1 = status === VerificationStatus.CLARIFICATION_STEP_1;
    const isInClarificationStep2 = status === VerificationStatus.CLARIFICATION_STEP_2;
    const isInClarification = isInClarificationStep1 || isInClarificationStep2;

    const isStep1 =
      status === VerificationStatus.STEP_1_SUBMITTED ||
      status === VerificationStatus.STEP_1_REVIEW ||
      status === VerificationStatus.CLARIFICATION_STEP_1;

    const onStepOne = status === VerificationStatus.STEP_1_SUBMITTED || status === VerificationStatus.STEP_1_REVIEW;
    const onStepTwo = status === VerificationStatus.STEP_2_SUBMITTED || status === VerificationStatus.STEP_2_REVIEW;

    const buttonsLockedStepOne = !isStep1 || !isOpenTaskAssignedToCurrentUser;
    const buttonsLockedStepTwo = !isOpenTaskAssignedToCurrentUser;

    const stepOneApproved = verificationData?.identification_status && verificationData?.business_licence_status;
    const stepTwoApproved = verificationData?.power_of_attorney_status;

    const systempartner = verificationData?.systempartner;
    const isSystemPartnerFull = systempartner?.integration === 'full';

    const updateRequest = verificationData?.update_request;
    const durNotCompleted = updateRequest?.status !== VerificationStatus.COMPLETED;
    const isVerificationDataChange = !!updateRequest?.id;

    const hasHandwerkskarteErrors = (verificationData?.hk_errors?.length ?? 0) > 0;

    const disableClarificationStepOne =
      !hasHandwerkskarteErrors &&
      ((verificationData?.id_errors == null && verificationData?.business_licence_status) ||
      (verificationData?.bl_errors == null && verificationData?.identification_status) ||
      (verificationData?.bl_errors == null && verificationData?.id_errors == null) ||
      (verificationData?.business_licence_status && verificationData?.identification_status) ||
      (verificationData?.id_errors === null && !verificationData?.identification_status) ||
      (verificationData?.bl_errors === null && !verificationData?.business_licence_status));

    const disableClarificationStepTwo = !(verificationData?.poa_errors && verificationData?.poa_errors?.length > 0);

    const isClarificationButtonDisabled = !!(
      !isOpenTaskAssignedToCurrentUser ||
      (onStepOne && disableClarificationStepOne) ||
      (onStepTwo && disableClarificationStepTwo) ||
      isInClarificationStep1 ||
      isInClarificationStep2
    );

    const idNotTouched = !verificationData?.identification_status && verificationData?.id_errors?.length === 0;
    const blNotTouched = !verificationData?.business_licence_status && verificationData?.bl_errors?.length === 0;
    const poaNotTouched = !verificationData?.power_of_attorney_status && verificationData?.poa_errors?.length === 0;

    const shouldDisableSendPoa = !(
      status === VerificationStatus.WAITING_ON_POA ||
      status === VerificationStatus.STEP_2_REVIEW
    );

    const isManualPoaUploadAllowed = waitingOnPoa || isInClarificationStep2;

    return {
      isCompleted,
      isRejected,
      waitingOnPoa,
      isInClarificationStep1,
      isInClarificationStep2,
      isInClarification,
      isStep1,
      onStepOne,
      onStepTwo,
      buttonsLockedStepOne,
      buttonsLockedStepTwo,
      stepOneApproved,
      stepTwoApproved,
      isOpenTaskAssignedToCurrentUser,
      hasOpenTask: openTask != null,
      openTask,
      systempartner,
      isSystemPartnerFull,
      updateRequest,
      durNotCompleted,
      isVerificationDataChange,
      isClarificationButtonDisabled,
      idNotTouched,
      blNotTouched,
      poaNotTouched,
      shouldDisableSendPoa,
      isManualPoaUploadAllowed,
    };
  }, [verificationData, currentUserId]);

  // --- Document Submit Handler ---
  const submitDocument = (
    docType: 'identification' | 'business_licence' | 'power_of_attorney' | 'handwerkskarte',
    errors: string[],
    fileUuid: string | undefined,
    reasonForReplacement?: string,
    callbacks?: { onSuccess?: () => void },
  ) => {
    const validKey = `${docType}_valid` as const;
    const errorsKey = `${docType}_errors` as const;
    const uuidKey = `${docType}_uuid` as const;

    const isValid = errors.length === 0;
    const params = {
      [validKey]: isValid,
      ...(isValid ? {} : { [errorsKey]: errors }),
      [uuidKey]: fileUuid,
      ...(reasonForReplacement ? { files_update_reason: reasonForReplacement } : {}),
    };

    updateDocsMutation.mutate(params, {
      onSuccess: () => {
        const toastText =
          docType === 'power_of_attorney' && !reasonForReplacement
            ? t('workshop_verification_request:toast.success.poa_saved')
            : t('workshop_verification_request:toast.success.document');
        toast({ title: toastText, status: 'success' });
        callbacks?.onSuccess?.();
      },
    });
  };

  // --- Clarification Handlers ---
  const onClarification = (step: 'step1' | 'step2') => {
    const status = step === 'step1' ? VerificationStatus.CLARIFICATION_STEP_1 : VerificationStatus.CLARIFICATION_STEP_2;
    clarificationMutation.mutate(
      { status },
      {
        onSuccess: () => {
          toast({
            title: t('workshop_verification_request:toast.success.clarification'),
            status: 'success',
          });
        },
      },
    );
  };

  // --- Approve Handler ---
  const approveStep = (callbacks?: { onSuccess?: () => void }) => {
    const { stepOneApproved, stepTwoApproved } = derived;

    if (stepOneApproved && !stepTwoApproved) {
      approveMutation.mutate(
        { status: VerificationStatus.WAITING_ON_POA },
        {
          onSuccess: () => {
            callbacks?.onSuccess?.();
            toast({
              title: t('workshop_verification_request:toast.success.verification.completed'),
              status: 'success',
            });
          },
        },
      );
    } else if (stepTwoApproved) {
      approveMutation.mutate(
        { status: VerificationStatus.COMPLETED },
        {
          onSuccess: () => {
            callbacks?.onSuccess?.();
            setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
            toast({
              title: t('workshop_verification_request:toast.success.verification.approved'),
              status: 'success',
            });
          },
        },
      );
    }
  };

  // --- Reject Handler ---
  const rejectVerification = (callbacks?: { onSuccess?: () => void }) => {
    approveMutation.mutate(
      { status: VerificationStatus.REJECTED },
      {
        onSuccess: () => {
          callbacks?.onSuccess?.();
          toast({
            title: t('workshop_verification_request:reject_verification.toast.success'),
            status: 'success',
          });
        },
      },
    );
  };

  // --- Submit from Clarification ---
  const submitFromClarification = (reasonForReplacement: string, callbacks?: { onSuccess?: () => void }) => {
    const targetStatus = derived.isInClarificationStep1
      ? VerificationStatus.STEP_1_SUBMITTED
      : VerificationStatus.STEP_2_SUBMITTED;

    approveMutation.mutate(
      { status: targetStatus },
      {
        onSuccess: () => {
          callbacks?.onSuccess?.();
          sendMessage({
            id: String(workshopId),
            text: { text: reasonForReplacement },
            resourceKey: 'workshop-verification-request',
          });
          setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth' }), 150);
          toast({
            title: t('workshop_verification_request:toast.success.verification.submitted'),
            status: 'success',
          });
        },
      },
    );
  };

  // --- Trigger SPF Mail ---
  const onTriggerSpfMail = () => {
    triggerSpfMailMutation.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: t('workshop_verification_request:toast.success.verification_mail_sent'),
          status: 'success',
        });
      },
    });
  };

  return {
    ...derived,
    mutations: {
      updateDocsMutation,
      clarificationMutation,
      approveMutation,
      assignMutation,
      triggerPoaMutation,
      triggerSpfMailMutation,
      fileUploadMutation,
    },
    actions: {
      queryToInvalidate,
      submitDocument,
      onClarification,
      approveStep,
      rejectVerification,
      submitFromClarification,
      onTriggerSpfMail,
    },
  };
}
